import React from 'react';
import {
  CATEGORY_LABELS,
  OBJECT_TYPE_LABELS,
} from '../../features/equipment/equipmentConstants';

interface Props {
  x: number;
  y: number;
  containerWidth: number;
  containerHeight: number;
  name: string;
  objectType: string;
  category: string;
  process?: string | null;
  description?: string;
  tags?: string[];
  templateName?: string | null;
  rotation: number;
  portCount: number;
  notes?: string;
  color: string;
  /** World-space footprint centroid X. */
  coordX?: number;
  /** World-space footprint centroid Y. */
  coordY?: number;
  /** Footprint width in world units. */
  width?: number;
  /** Footprint height in world units. */
  height?: number;
  /** Layout save vs DXF extract vs manual. */
  sourceLabel?: string;
}

const TOOLTIP_W = 260;
const OFFSET = 16;

const formatCoord = (n: number): string =>
  Number.isFinite(n) ? n.toFixed(2) : '—';

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start gap-2 leading-tight">
    <span className="text-gray-400 shrink-0 w-[72px]">{label}</span>
    <span className="text-gray-800 flex-1 min-w-0 break-words">{children}</span>
  </div>
);

const EquipmentTooltip: React.FC<Props> = ({
  x,
  y,
  containerWidth,
  containerHeight,
  name,
  objectType,
  category,
  process,
  description,
  tags,
  templateName,
  rotation,
  portCount,
  notes,
  color,
  coordX,
  coordY,
  width,
  height,
  sourceLabel,
}) => {
  const left =
    x + OFFSET + TOOLTIP_W > containerWidth ? Math.max(8, x - OFFSET - TOOLTIP_W) : x + OFFSET;
  const top = Math.min(Math.max(8, y + OFFSET), Math.max(8, containerHeight - 240));

  const typeLabel = OBJECT_TYPE_LABELS[objectType as keyof typeof OBJECT_TYPE_LABELS] ?? objectType;
  const categoryLabel = CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS] ?? category;
  const hasCoords = typeof coordX === 'number' && typeof coordY === 'number';
  const hasSize = typeof width === 'number' && typeof height === 'number';

  return (
    <div
      className="pointer-events-none absolute z-20 rounded-lg border border-gray-200 bg-white/95 shadow-xl backdrop-blur-sm px-3 py-2 text-[11px] space-y-1"
      style={{ left, top, width: TOOLTIP_W }}
    >
      <div className="flex items-center gap-2 pb-1 mb-1 border-b border-gray-100">
        <span
          className="inline-block w-3 h-3 rounded-sm border border-black/20 shrink-0"
          style={{ background: color }}
        />
        <span className="font-semibold text-gray-900 truncate">{name || 'Equipment'}</span>
      </div>

      <Row label="Type">{typeLabel}</Row>
      {sourceLabel && <Row label="Source">{sourceLabel}</Row>}
      <Row label="Category">{categoryLabel}</Row>
      {process && <Row label="Process">{process}</Row>}
      {templateName && <Row label="Template">{templateName}</Row>}
      {description && <Row label="Description">{description}</Row>}
      {tags && tags.length > 0 && (
        <Row label="Tags">
          <span className="flex flex-wrap gap-1">
            {tags.map((tag) => (
              <span
                key={tag}
                className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px]"
              >
                {tag}
              </span>
            ))}
          </span>
        </Row>
      )}
      {hasCoords && (
        <Row label="Coords">{`X ${formatCoord(coordX!)} · Y ${formatCoord(coordY!)}`}</Row>
      )}
      {hasSize && (
        <Row label="Size">{`${formatCoord(width!)} × ${formatCoord(height!)}`}</Row>
      )}
      <Row label="Rotation">{`${Math.round(rotation)}°`}</Row>
      {sourceLabel === 'Layout save' && <Row label="Ports">{portCount}</Row>}
      {notes && <Row label="Notes">{notes}</Row>}
    </div>
  );
};

export default EquipmentTooltip;
